import { LEGAL, formatAddress } from "@/config/legal";
import { ScrollText } from "lucide-react";

/** Small reusable section header. */
const H = ({ children }) => (
  <h2 className="mt-10 font-serif text-2xl md:text-3xl text-[#0A1128]">{children}</h2>
);
const P = ({ children }) => (
  <p className="mt-3 text-slate-700 text-[15px] leading-relaxed">{children}</p>
);

export default function Terms() {
  return (
    <div className="scent-wave min-h-[calc(100vh-4rem)]">
      <div className="max-w-3xl mx-auto px-6 md:px-10 py-16" data-testid="terms-page">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center">
            <ScrollText className="w-5 h-5 text-emerald-700" />
          </div>
          <div className="text-[10px] tracking-[0.28em] uppercase text-emerald-700 font-semibold">Legal</div>
        </div>
        <h1 className="mt-4 font-serif text-5xl md:text-6xl tracking-tight text-[#0A1128]">Terms of Service</h1>
        <div className="mt-3 text-xs text-slate-500">
          Effective {LEGAL.effectiveDate} · Last updated {LEGAL.lastUpdated}
        </div>

        <P>
          These Terms of Service (&ldquo;<b>Terms</b>&rdquo;) govern your access to and use of the {LEGAL.brandMark} website,
          web application, and related services (collectively, the &ldquo;<b>Service</b>&rdquo;) operated by{" "}
          <b>{LEGAL.companyLegalName}</b> (&ldquo;<b>{LEGAL.dba}</b>,&rdquo; &ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;).
          By creating an account, browsing the Service, or making a purchase you agree to be bound by these Terms.
          If you do not agree, do not use the Service.
        </P>

        <H>1. The Service</H>
        <P>
          {LEGAL.dba} provides a hospitality-freshness verification platform that lets short-term-rental hosts apply
          for a {LEGAL.brandMark} certification, subscribe to on-going monitoring, and lets guests browse and identify
          certified stays. Certifications reflect information hosts provide to us and our automated / manual review at
          the time of issuance; they are not medical, allergen-free, or absolute guarantees of any physical condition
          in a property.
        </P>

        <H>2. Eligibility &amp; Accounts</H>
        <P>
          You must be at least 18 years old and legally capable of forming a binding contract. When you register you
          agree to (a) provide accurate information, (b) keep your credentials secure, and (c) accept responsibility
          for all activity under your account. Hosts additionally represent that they have the legal right to list any
          property they add to the Service.
        </P>

        <H>3. Fees, Billing &amp; Automatic Renewal</H>
        <P>
          Current published pricing is listed on our <a href="/pricing" className="text-emerald-700 underline">Pricing page</a>. Payments are processed by Stripe, Inc. You authorize us and Stripe to charge your payment
          method for all fees you incur. Subscription plans (Monthly Monitoring, Yearly Monitoring) automatically renew
          at the end of each billing period unless you cancel before the renewal date. Cancellation stops future
          renewals; it does not entitle you to a refund of amounts already paid except as described in our{" "}
          <a href="/refund" className="text-emerald-700 underline">Refund Policy</a>.
        </P>

        <H>4. Certification Decisions</H>
        <P>
          {LEGAL.brandMark} certifications are issued at our sole discretion. We may approve, reject, suspend, downgrade,
          or revoke a certification at any time based on (i) information the host provides, (ii) our review of that
          information, (iii) guest reports, (iv) data from any {LEGAL.brandMark} Verify™ device installed at the property,
          or (v) violations of these Terms. Certification badges, seals, marks, and public certification pages remain
          our intellectual property; licenses to display them terminate automatically when a certification is revoked.
        </P>

        <H>5. User Content</H>
        <P>
          You retain ownership of photos, descriptions, and other content you upload (&ldquo;<b>User Content</b>&rdquo;),
          but you grant {LEGAL.dba} a worldwide, non-exclusive, royalty-free license to host, reproduce, adapt, publish,
          and display User Content solely to operate, promote, and improve the Service. You represent that you own or
          have all necessary rights to the User Content you submit.
        </P>

        <H>6. Prohibited Conduct</H>
        <P>You agree not to:</P>
        <ul className="mt-2 list-disc pl-6 space-y-1.5 text-[15px] text-slate-700">
          <li>Falsify property ownership or certification information;</li>
          <li>Display a {LEGAL.brandMark} certification badge for a property that is not currently certified;</li>
          <li>Reverse-engineer, scrape, or interfere with the Service;</li>
          <li>Use the Service to violate applicable law or third-party rights.</li>
        </ul>

        <H>7. Third-Party Services</H>
        <P>
          The Service integrates with third parties including Stripe (payments), Emergent Resend (email), Emergent
          Object Storage (media hosting), and — if a host connects it — Google Calendar. Your use of those third-party
          services is subject to their own terms. We are not responsible for third-party outages or acts.
        </P>

        <H>8. Intellectual Property</H>
        <P>
          {LEGAL.brandMark}, the {LEGAL.brandMark} logos, &ldquo;See It. Smell It. Book It.™,&rdquo; the freshness gauge
          design, and all related marks are trademarks of {LEGAL.companyLegalName}. All rights not expressly granted are
          reserved.
        </P>

        <H>9. Disclaimers</H>
        <P>
          THE SERVICE IS PROVIDED &ldquo;AS IS&rdquo; AND &ldquo;AS AVAILABLE&rdquo; WITHOUT WARRANTIES OF ANY KIND,
          EXPRESS OR IMPLIED, INCLUDING WITHOUT LIMITATION MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND
          NON-INFRINGEMENT. {LEGAL.brandMark} DOES NOT GUARANTEE THAT A CERTIFIED PROPERTY WILL BE FREE OF ODORS,
          ALLERGENS, OR OTHER CONDITIONS AT THE TIME OF A GUEST&apos;S STAY.
        </P>

        <H>10. Limitation of Liability</H>
        <P>
          TO THE MAXIMUM EXTENT PERMITTED BY LAW, {LEGAL.companyLegalName.toUpperCase()} SHALL NOT BE LIABLE FOR ANY
          INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES. OUR
          AGGREGATE LIABILITY FOR ANY CLAIM RELATING TO THE SERVICE SHALL NOT EXCEED THE GREATER OF (a) US $100 OR
          (b) THE AMOUNT YOU PAID US IN THE 12 MONTHS BEFORE THE CLAIM AROSE.
        </P>

        <H>11. Indemnification</H>
        <P>
          You agree to indemnify and hold harmless {LEGAL.companyLegalName}, its officers, employees, and agents from
          any claim, damage, or expense (including reasonable attorneys&apos; fees) arising out of your use of the
          Service, your User Content, or your violation of these Terms.
        </P>

        <H>12. Termination</H>
        <P>
          We may suspend or terminate your access to the Service at any time, with or without notice, for conduct that
          we believe violates these Terms or is harmful to other users or to us. You may terminate your account at any
          time by contacting {LEGAL.legalEmail}.
        </P>

        <H>13. Governing Law &amp; Disputes</H>
        <P>
          These Terms are governed by {LEGAL.governingLaw}. Any dispute shall be brought exclusively in the state or
          federal courts located in the {LEGAL.jurisdiction}, and you consent to the personal jurisdiction of those courts.
        </P>

        <H>14. Changes to These Terms</H>
        <P>
          We may update these Terms from time to time. If we make material changes we will notify you by email and by
          posting a notice on the Service before the changes take effect. Your continued use of the Service after the
          effective date constitutes acceptance of the updated Terms.
        </P>

        <H>15. Contact</H>
        <P>
          Questions? Reach us at{" "}
          <a href={`mailto:${LEGAL.legalEmail}`} className="text-emerald-700 underline">{LEGAL.legalEmail}</a>.
          Registered mailing address is on file with our payment processor and available upon written request.
        </P>

        <p className="mt-10 text-xs text-slate-400">
          © {new Date().getFullYear()} {LEGAL.brandMark}. Guest Smell and the Guest Smell logos are trademarks of the business behind {LEGAL.brandMark}.
        </p>
      </div>
    </div>
  );
}
